import {
    CategoryViewType,
    ConfigApi,
    DineInType,
    MenuProductViewType,
    MultistoreLayoutType,
    OrderSchedulingMode,
    OrderType
} from "../Api/types";
import AppConfig from "../AppConfig";
import Utils from "../../utils/Utils";
import {SessionState} from "../Session/types";

class StoreUtils {
    static isHomepageMenu(config?: ConfigApi) {
        return !config?.homepage_full;
    }

    static isMultistore(config?: ConfigApi) {
        return config?.type === "MULTISTORE";
    }

    static isOrderingEnabled(config?: ConfigApi) {
        return config?.ordering.mode && config?.ordering.mode === "ENABLED";
    }

    static isOrderingReadonly(config?: ConfigApi) {
        return config?.ordering.mode && config?.ordering.mode === "READ_ONLY";
    }

    static isTable(session?: SessionState) {
        return session?.order_type === "DINE_IN" && session.table_number;
    }

    static isPaused(config?: ConfigApi) {
        return config?.status === "PAUSED"
    }

    static mergeConfigs(defaultConfig?: ConfigApi, config?: ConfigApi) {
        if (!config) return defaultConfig;
        if (!defaultConfig) return config;
        let newConfig = JSON.parse(JSON.stringify(config)) as ConfigApi;
        if (!newConfig.layout.cover_large) {
            newConfig.layout.cover_large = defaultConfig.layout.cover_large;
        }
        if (!newConfig.layout.image_link) {
            newConfig.layout.image_link = defaultConfig.layout.image_link;
        }
        return newConfig;
    }

    static getOrderTypes(config: ConfigApi): OrderType[] {
        const orderTypes: OrderType[] = [];
        if (config.ordering.takeaway) orderTypes.push(OrderType.PICK_UP);
        if (config.ordering.delivery) orderTypes.push(OrderType.DELIVERY);
        if (config.ordering.dine_in) orderTypes.push(OrderType.DINE_IN);
        if (config.ordering.room_service) orderTypes.push(OrderType.ROOM_SERVICE);

        return orderTypes;
    }

    static getDineInTypes(config: ConfigApi | undefined): DineInType[] {
        if (!config) return [];

        return [config?.ordering?.dine_in_counter ? DineInType.COUNTER : undefined,
            config?.ordering?.dine_in_table ? DineInType.TABLE : undefined]
            .filter(Boolean) as DineInType[]
    }

    static getDomainFromUrl(urlString: string) {
        const url = new URL(urlString);
        const segments = url.pathname.split('/').filter(Boolean);
        if (AppConfig.isMultistore()) {
            if (segments.length < 1) return undefined;
            if (segments.includes("inner")) {
                if (segments.length < 2) return undefined;
                return `${segments[1]}/${segments[2]}`
            }
            const subdomain = Utils.getSubdomainFromHostname(urlString);
            return `${subdomain}/${segments[0]}`
        }
        if (segments.includes("inner")) {
            if (segments.length < 1) return false;
            return `${segments[1]}`
        }
        const subdomain = Utils.getSubdomainFromHostname(urlString);
        return `${subdomain}`

    }

    static isSameDomain(urlString: string, storeDomain?: string) {
        if (!storeDomain) return false;
        const url = new URL(urlString);
        if (url.hostname !== window.location.hostname) return false;
        if (url.hostname === storeDomain) return true;
        const urlStoreDomain = this.getDomainFromUrl(urlString);
        if (!urlStoreDomain) return false;
        if (urlStoreDomain !== storeDomain) {
            if (urlStoreDomain.includes("/")) {
                if (storeDomain === urlStoreDomain.split("/")[1]) return true;
            }
            return false;
        }
        return true;
    }

    static isItemCommentAvailable(config?: ConfigApi) {
        if (!config) return false;

        return config.ordering.item_comment;
    }

    static getMenuCategoryViewType(config?: ConfigApi): CategoryViewType {
        return config?.old_settings?.menu_category_view_type === CategoryViewType.IMAGE ? CategoryViewType.IMAGE : CategoryViewType.BASIC;
    }

    static getMenuProductsViewType(config?: ConfigApi): MenuProductViewType {
        const viewType = config?.old_settings?.menu_products_view_type;

        switch (viewType) {
            case "HORIZONTAL":
                return MenuProductViewType.HORIZONTAL;
            case "VERTICAL_BIG_IMAGE":
                return MenuProductViewType.VERTICAL_BIG_IMAGE;
            case "VERTICAL_SMALL_IMAGE":
            default:
                return MenuProductViewType.VERTICAL_SMALL_IMAGE;
        }
    }

    static getMultistoreLayoutType(config: ConfigApi): MultistoreLayoutType {
        const layoutType = config.layout.multistore_layout;

        switch (layoutType) {
            case "BRAND_LIST":
                return MultistoreLayoutType.BRAND_LIST;
            case "HOME_WITH_MAP":
            default:
                return MultistoreLayoutType.HOME_WITH_MAP;
        }
    }

    static isMultilevelMenu(config: ConfigApi | undefined): boolean {
        if (!config) return false;

        return config.layout.menu_multilevel;
    }

    static getOrderSchedulingMode(config: ConfigApi | undefined) {
        const schedulingMode = config?.ordering?.order_scheduling_mode;

        return schedulingMode ? schedulingMode as OrderSchedulingMode : OrderSchedulingMode.DISABLED;
    }

    static isPickDateEnabled(config: ConfigApi | undefined) {
        const orderSchedulingMode = StoreUtils.getOrderSchedulingMode(config);

        return orderSchedulingMode === OrderSchedulingMode.ENABLED || orderSchedulingMode === OrderSchedulingMode.SAME_DAY;
    }

    static isAgeRestrictionDisabled(config: ConfigApi | undefined) {
        if (!config) return false;

        return !!config.old_settings.age_restricted_disabled;
    }
}

export default StoreUtils;
